/**
 * ADR-0176 §12 — per-account smoke-test opt-in toggle.
 *
 * Sibling of `EntitlementOverridesCard` on the account detail view.
 * PATCHes `smoke_test_opt_in` via the same `updateAccount` handler
 * that already handles `name` / `status` / `is_internal` — the server
 * atomically appends `agent_sill_heartbeat` to the account's r01
 * allowlist when the flag flips false → true.
 *
 * "Run smoke now" starts a run (`POST /internal/admin/smoke-runs`) and
 * hands off to the top-level `SmokeRunsView` with the new run pre-opened
 * (`setView('smoke', { smokeRunId })`), so the staff lands directly on
 * its plan + progress instead of an empty board.
 *
 * ADR-0176 addendum (2026-07-10) — three ad-hoc "Test:" trigger
 * buttons (browse_catalog / check_availability / recommend) live
 * BELOW the guided run controls. Each button calls the new
 * `startAdHocSmokeRun` client method, shows a per-button loading
 * state, and renders the terminal result inline as a small pill
 * next to the row (no navigation required — the result also appears
 * in the `SmokeRunsView` list once the addendum wires `run_kind`
 * into the row rendering).
 */

import { useState } from 'react';

import type {
  AdminAccountSite,
  AdminAccountUpdateResponse,
  AdminAdHocSmokeSkill,
} from '@sill/types/api';

import { useAdminApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import type { SetView } from '../../app/view-router.js';
import { Card } from '../../components/atoms/Card.js';
import { Pill } from '../../components/atoms/Pill.js';
import type { PillTone } from '../../components/atoms/Pill.js';
import { useC } from '../../lib/theme.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

export interface SmokeOptInCardProps {
  readonly accountId: string;
  readonly optedIn: boolean;
  readonly sites: readonly AdminAccountSite[];
  readonly onUpdated: (next: AdminAccountUpdateResponse['account']) => void;
  readonly setView: SetView;
}

/**
 * ADR-0176 addendum — the three ad-hoc buttons. Kept as a small
 * data table (not a hard-coded list of JSX blocks) so the loop that
 * renders them is trivially auditable.
 */
const AD_HOC_TRIGGERS: readonly {
  readonly skill: AdminAdHocSmokeSkill;
  readonly label: string;
}[] = [
  { skill: 'browse_catalog', label: 'Test: Browse catalog' },
  { skill: 'check_availability', label: 'Test: Check availability' },
  { skill: 'recommend', label: 'Test: Recommend' },
];

/**
 * Terminal-state result of an ad-hoc call. `null` before the
 * request; after the response we render one of these next to the
 * button that fired the call.
 */
type AdHocResult =
  | { readonly kind: 'passed' }
  | { readonly kind: 'failed'; readonly reason: string }
  | { readonly kind: 'error'; readonly reason: string };

/**
 * Pill tone selector for an ad-hoc result. Same visual language as
 * `SmokeRunsView`'s `skillTone`.
 */
function resultTone(result: AdHocResult): PillTone {
  if (result.kind === 'passed') return 'mint';
  if (result.kind === 'failed') return 'coral';
  return 'coral';
}

/**
 * Human-readable label for the inline pill.
 */
function resultLabel(result: AdHocResult): string {
  if (result.kind === 'passed') return 'Passed';
  if (result.kind === 'failed') {
    return result.reason.length > 0 ? `Failed: ${result.reason}` : 'Failed';
  }
  return result.reason.length > 0 ? `Error: ${result.reason}` : 'Error';
}

export function SmokeOptInCard({
  accountId,
  optedIn,
  sites,
  onUpdated,
  setView,
}: SmokeOptInCardProps): JSX.Element {
  const C = useC();
  const api = useAdminApi();
  const [saving, setSaving] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [adHocRunning, setAdHocRunning] = useState<AdminAdHocSmokeSkill | null>(
    null,
  );
  const [adHocResults, setAdHocResults] = useState<
    Partial<Record<AdminAdHocSmokeSkill, AdHocResult>>
  >({});

  // ADR-0176 addendum — pick the first eligible site for ad-hoc
  // dispatch. `AdminAccountSite` doesn't carry a per-site
  // `has_catalog` signal on the wire yet; when the account has
  // multiple sites the staff falls through to the guided run for
  // now. First non-suspended site keeps the mental model simple
  // (matches how `readSmokePlanSites` orders in `plan.ts`).
  const eligibleSites = sites.filter(
    (s) => s.status === 'discovery_active' || s.status === 'transactional_active',
  );
  // The staff picks which site to test (guided run + ad-hoc). Defaults to
  // the first live site; the picker only lists live (non-deleted,
  // proven) sites, the same set the server accepts.
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(
    eligibleSites[0]?.site_id ?? null,
  );
  const targetSite =
    eligibleSites.find((s) => s.site_id === selectedSiteId)
    ?? eligibleSites[0]
    ?? null;
  const adHocEnabled = optedIn && targetSite !== null;

  const toggle = async (): Promise<void> => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      const res = await api.updateAccount(accountId, {
        smoke_test_opt_in: !optedIn,
      });
      onUpdated(res.account);
    } catch (err) {
      const reason =
        err instanceof ApiError && err.server_reason !== undefined
          ? `: ${err.server_reason}`
          : '';
      setError(`Could not update opt-in${reason}.`);
    } finally {
      setSaving(false);
    }
  };

  const runNow = async (): Promise<void> => {
    if (starting || !optedIn) return;
    setStarting(true);
    setError(null);
    try {
      const res = await api.startSmokeRun(accountId, targetSite?.site_id);
      setView('smoke', { smokeRunId: res.smoke_run_id });
    } catch (err) {
      const reason =
        err instanceof ApiError && err.server_reason !== undefined
          ? `: ${err.server_reason}`
          : '';
      setError(`Could not start smoke run${reason}.`);
    } finally {
      setStarting(false);
    }
  };

  const runAdHoc = async (skill: AdminAdHocSmokeSkill): Promise<void> => {
    if (adHocRunning !== null || !adHocEnabled) return;
    if (targetSite === null) return;
    setAdHocRunning(skill);
    setError(null);
    // Clear the previous result for this skill so a re-click renders
    // a fresh loading state.
    setAdHocResults((prev) => {
      const next = { ...prev };
      delete next[skill];
      return next;
    });
    try {
      const res = await api.startAdHocSmokeRun(
        accountId,
        targetSite.site_id,
        skill,
      );
      const outcome = res.skill_result.outcome ?? 'error';
      if (outcome === 'passed') {
        setAdHocResults((prev) => ({ ...prev, [skill]: { kind: 'passed' } }));
      } else if (outcome === 'failed') {
        setAdHocResults((prev) => ({
          ...prev,
          [skill]: {
            kind: 'failed',
            reason: res.skill_result.reason_class ?? '',
          },
        }));
      } else {
        setAdHocResults((prev) => ({
          ...prev,
          [skill]: {
            kind: 'error',
            reason: res.skill_result.reason_class ?? '',
          },
        }));
      }
    } catch (err) {
      const reason =
        err instanceof ApiError && err.server_reason !== undefined
          ? err.server_reason
          : 'unavailable';
      setAdHocResults((prev) => ({
        ...prev,
        [skill]: { kind: 'error', reason },
      }));
    } finally {
      setAdHocRunning(null);
    }
  };

  return (
    <Card style={{ padding: 20 }}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: 2,
            textTransform: 'uppercase',
          }}
        >
          Smoke-test opt-in
        </div>
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.dim,
            letterSpacing: 1,
          }}
        >
          Test-mode Stripe only
        </div>
      </div>

      <div
        style={{
          fontFamily: sansFont,
          fontSize: 12,
          color: C.muted,
          lineHeight: 1.5,
          marginBottom: 12,
        }}
      >
        Periodic Sill Heartbeat agent runs the seven-skills end-to-end
        smoke against this account. Off by default. Test-mode Stripe
        only. When enabled, Heartbeat is automatically added to any
        r01 allowlist on the account&rsquo;s sites — a policy without
        r01 stays untouched.
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: optedIn ? C.mintFg : C.dim,
              fontWeight: 500,
            }}
          >
            {optedIn ? 'Enabled' : 'Disabled'}
          </span>
          {error !== null ? (
            <span
              style={{
                fontFamily: sansFont,
                fontSize: 12,
                color: C.coralFg,
              }}
            >
              {error}
            </span>
          ) : null}
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {eligibleSites.length > 0 ? (
            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                fontFamily: monoFont,
                fontSize: 10,
                letterSpacing: 1.2,
                textTransform: 'uppercase',
                color: C.muted,
                flex: '1 1 240px',
                minWidth: 0,
                maxWidth: '100%',
              }}
            >
              Test site
              <select
                value={targetSite?.site_id ?? ''}
                onChange={(e) => setSelectedSiteId(e.target.value)}
                disabled={eligibleSites.length < 2}
                style={{
                  fontFamily: monoFont,
                  fontSize: 12,
                  textTransform: 'none',
                  letterSpacing: 0,
                  color: C.text,
                  background: C.surface,
                  border: `1px solid ${C.border}`,
                  borderRadius: 2,
                  padding: '8px 10px',
                  minHeight: 44,
                  flex: '1 1 auto',
                  width: '100%',
                  minWidth: 0,
                  maxWidth: '100%',
                  textOverflow: 'ellipsis',
                }}
              >
                {eligibleSites.map((s) => (
                  <option key={s.site_id} value={s.site_id}>
                    {s.domain}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <button
            type="button"
            disabled={!optedIn || starting}
            onClick={(): void => void runNow()}
            style={{
              background: 'transparent',
              color: optedIn ? C.text : C.dim,
              fontFamily: sansFont,
              fontSize: 13,
              fontWeight: 500,
              padding: '10px 16px',
              borderRadius: 2,
              border: `1px solid ${optedIn ? C.border : C.borderSoft}`,
              cursor: optedIn && !starting ? 'pointer' : 'not-allowed',
              opacity: starting ? 0.6 : 1,
              minHeight: 44,
            }}
            aria-disabled={!optedIn || starting}
          >
            {starting ? 'Starting…' : 'Run smoke now'}
          </button>
          <button
            type="button"
            disabled={!optedIn}
            onClick={() => setView('smoke')}
            style={{
              background: 'transparent',
              color: optedIn ? C.muted : C.dim,
              fontFamily: sansFont,
              fontSize: 12,
              fontWeight: 500,
              padding: '10px 12px',
              borderRadius: 2,
              border: 0,
              cursor: optedIn ? 'pointer' : 'not-allowed',
              minHeight: 44,
              textDecoration: 'underline',
            }}
            aria-disabled={!optedIn}
          >
            View past runs
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={(): void => void toggle()}
            style={{
              background: optedIn ? C.surface2 : C.lime,
              color: optedIn ? C.text : C.ink,
              fontFamily: sansFont,
              fontSize: 13,
              fontWeight: 600,
              padding: '10px 16px',
              borderRadius: 2,
              border: optedIn ? `1px solid ${C.border}` : '0',
              opacity: saving ? 0.6 : 1,
              minHeight: 44,
            }}
          >
            {saving ? 'Saving…' : optedIn ? 'Disable' : 'Enable'}
          </button>
        </div>
      </div>

      {/* ADR-0176 addendum (2026-07-10) — ad-hoc single-skill mode.
          Three individual triggers restricted to the non-transactional
          reads. Each row shows the button + inline pass/fail pill; the
          result also lands in `SmokeRunsView` (label 'ad hoc' vs
          'guided') for history/audit. */}
      <div
        style={{
          marginTop: 18,
          paddingTop: 14,
          borderTop: `1px solid ${C.borderSoft}`,
        }}
      >
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: 2,
            textTransform: 'uppercase',
            marginBottom: 8,
          }}
        >
          Ad-hoc read-only tests
        </div>
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 12,
            color: C.muted,
            lineHeight: 1.5,
            marginBottom: 10,
          }}
        >
          Run a single read skill against{' '}
          {targetSite !== null ? (
            <span style={{ fontFamily: monoFont, color: C.text }}>
              {targetSite.domain}
            </span>
          ) : (
            <span style={{ color: C.dim }}>no site available</span>
          )}
          . Non-transactional only — no charges, no refunds; safe to
          run against live-mode accounts.
        </div>
        <div className="grid grid-cols-1 gap-2">
          {AD_HOC_TRIGGERS.map((trigger) => {
            const result = adHocResults[trigger.skill];
            const running = adHocRunning === trigger.skill;
            const disabled = !adHocEnabled || adHocRunning !== null;
            return (
              <div
                key={trigger.skill}
                className="flex items-center justify-between gap-3 flex-wrap"
                style={{
                  padding: '8px 10px',
                  borderRadius: 2,
                  border: `1px solid ${C.borderSoft}`,
                  background: C.surface2,
                }}
              >
                <button
                  type="button"
                  disabled={disabled}
                  onClick={(): void => void runAdHoc(trigger.skill)}
                  style={{
                    background: 'transparent',
                    color: adHocEnabled ? C.text : C.dim,
                    fontFamily: sansFont,
                    fontSize: 13,
                    fontWeight: 500,
                    padding: '8px 12px',
                    borderRadius: 2,
                    border: `1px solid ${adHocEnabled ? C.border : C.borderSoft}`,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: running ? 0.6 : 1,
                    minHeight: 36,
                  }}
                  aria-disabled={disabled}
                >
                  {running ? 'Running…' : trigger.label}
                </button>
                <div className="flex items-center gap-2 flex-wrap">
                  {result !== undefined ? (
                    <Pill tone={resultTone(result)}>{resultLabel(result)}</Pill>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
}
